import { useMemo } from "react";
import {
	BarChart,
	Bar,
	XAxis,
	YAxis,
	Tooltip,
	ReferenceLine,
	ResponsiveContainer,
	Cell,
} from "recharts";
import { useContextAwareGetProjectResults } from "@hooks/index";
import { calculateCO2EmissionsByActivity } from "@pages/project/results/hooks/resultsUtils";
import { roundToTwoDecimals } from "./utils";
import { formatNumber } from "@utils/helperFunctions";
import GraphContainer from "./GraphContainer";
import Skeleton from "react-loading-skeleton";
import { darkenHex } from "@utils/color";
import TranslatableText from "@components/translations/TranslatableText";
import CustomTooltip from "./CustomTooltip";

const CO2ByActivityBarChart = ({
	width,
	height,
	className,
	projectId,
	selectedActivities,
}: {
	width?: string | number;
	height: string | number;
	className?: string;
	projectId: number;
	selectedActivities?: number[];
}) => {
	const { data: rawData, isFetching } = useContextAwareGetProjectResults(
		{ id: projectId, activities: selectedActivities },
		{
			refetchOnMountOrArgChange: true,
			skip: !projectId,
			selectFromResult: (result) => ({
				...result,
				data: calculateCO2EmissionsByActivity(result.data),
			}),
		}
	);

	// Sort data by emission value (descending: highest positive emissions first, then negative/sequestration)
	const data = useMemo(() => {
		if (!rawData) return rawData;
		return [...rawData].sort((a, b) => b.value - a.value);
	}, [rawData]);

	// Calculate color based on value and its position in the range
	const getColor = (value: number, maxAbsValue: number) => {
		const baseColor = value > 0 ? "#CC1212" : "#52A021";
		const absValue = Math.abs(value);
		// Calculate darkness percentage based on value (0-50% darken range)
		const darkenAmount = (absValue / maxAbsValue) * 30;
		return darkenHex(baseColor, darkenAmount);
	};

	// Calculate domain and max absolute value for coloring
	const { dataMin, dataMax, maxAbsValue } = useMemo(() => {
		if (!data || data.length === 0) {
			return { dataMin: 0, dataMax: 0, maxAbsValue: 0 };
		}

		const values = data.map((d) => d.value);
		const min = Math.min(...values);
		const max = Math.max(...values);
		const maxAbs = Math.max(Math.abs(min), Math.abs(max));

		return {
			dataMin: roundToTwoDecimals(min),
			dataMax: roundToTwoDecimals(max),
			maxAbsValue: maxAbs,
		};
	}, [data]);

	// Calculate dynamic height based on number of activities
	const dynamicHeight = useMemo(() => {
		if (!data || data.length === 0) return height;
		// Minimum 35px per bar, with some padding
		const calculatedHeight = data.length * 35 + 60;
		return typeof height === "number" 
			? Math.max(height, calculatedHeight)
			: calculatedHeight;
	}, [data, height]);

	return (
		<>
			<div
				className="mt-4 w-fit"
				style={{ borderBottom: "1px solid #000", padding: "4px 0" }}
			>
				<h2 className="ff-bold fs-13 text-black">
					<TranslatableText translationKey="project.co2_eq_fluxes_per_activity" />
				</h2>
			</div>
			<GraphContainer height={dynamicHeight} zIndex={10}>
				{isFetching ? (
					<Skeleton width={width ?? "80%"} height={dynamicHeight} />
				) : (
					<ResponsiveContainer
						width={width ?? "80%"}
						height={dynamicHeight}
						className={className}
					>
						<BarChart
							data={data}
							layout="vertical"
							margin={{
								top: 20,
								right: 25,
								left: 10,
								bottom: 20,
							}}
						>
							<XAxis
								type="number"
								style={{ fontSize: "12px" }}
								domain={[dataMin, dataMax]}
								tickFormatter={(value: number) => formatNumber(value)}
							/>
							<YAxis
								dataKey="name"
								type="category"
								width={140}
								style={{ fontSize: "11px" }}
							/>
							<Tooltip content={<CustomTooltip />} />

							<Bar dataKey="value" maxBarSize={25}>
								{data?.map((entry, index) => (
									<Cell
										key={`cell-${index}`}
										fill={getColor(entry.value, maxAbsValue)}
									/>
								))}
							</Bar>

							<ReferenceLine x={0} stroke="#000" strokeWidth={1} />
						</BarChart>
					</ResponsiveContainer>
				)}
			</GraphContainer>
		</>
	);
};

export default CO2ByActivityBarChart;

